import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
    FaThLarge, 
    FaUserPlus,
    FaUsers,
    FaBuilding,
    FaList,
    FaPlusCircle,
    FaBell,
    FaUserCircle,
    FaLock,
    FaSignOutAlt,
    FaChevronDown,
    FaTimes,
    FaCashRegister,
    FaCalendarAlt,
    FaHistory,
    FaFileAlt
} from 'react-icons/fa';
import { useMobileMenu } from '../../overall_manager/contexts/MobileMenuContext';
import './Sidebar.css';

const Sidebar = () => {
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingLeaveRequests, setPendingLeaveRequests] = useState(0);
    const [pendingLoanRequests, setPendingLoanRequests] = useState(0);
    const [openDropdown, setOpenDropdown] = useState(null);
    const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();
    const [userProfile, setUserProfile] = useState(null);
    const baseUrl = "https://api.burrayhr.com";

    useEffect(() => {
        fetchNotices();
        fetchPendingLeaveRequests();
        fetchPendingLoanRequests();
        fetchUserProfile();
        
        // Poll for new notices and requests every 30 seconds
        const noticesInterval = setInterval(fetchNotices, 30000);
        const leaveRequestsInterval = setInterval(fetchPendingLeaveRequests, 30000);
        const loanRequestsInterval = setInterval(fetchPendingLoanRequests, 30000);
        
        return () => {
            clearInterval(noticesInterval);
            clearInterval(leaveRequestsInterval);
            clearInterval(loanRequestsInterval);
        };
    }, []);

    const fetchUserProfile = async () => {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${baseUrl}/api/profile/`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserProfile(response.data);
    };


    const fetchNotices = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/notices/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Count unread notices
            const unreadNotices = response.data.filter(notice => !notice.is_read);
        } catch (error) {
            console.error('Error fetching notices:', error);
        }
    };

    const fetchPendingLeaveRequests = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/leaves/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Count pending leave requests
            const pendingRequests = response.data.filter(request => request.status === 'pending');
            setPendingLeaveRequests(pendingRequests.length);
        } catch (error) {
            console.error('Error fetching leave requests:', error);
        }
    };

    const fetchPendingLoanRequests = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/loans/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            // Count pending loan requests
            const pendingRequests = response.data.filter(request => request.status === 'pending');
            setPendingLoanRequests(pendingRequests.length);
        } catch (error) {
            console.error('Error fetching loan requests:', error);
        }
    };

    const handleMenuClick = (item) => {
        if (item.onClick) {
            item.onClick();
        }
        // If clicking on notices, mark notifications as read
        if (item.path === '/admin-manager/notices') {
            setUnreadCount(0);
        }
        // Close mobile menu when clicking a menu item
        if (window.innerWidth <= 768) {
            toggleMobileMenu();
        }
    };

    const menuItems = [
        {
            path: "/manager/dashboard",
            name: "Dashboard",
            icon: <FaThLarge />
        },
        {
            path: "/manager/employees",
            name: "Employees",
            icon: <FaUsers />
        },
        {
            path: "/manager/profile",
            name: "Profile",
            icon: <FaUserCircle />
        },
        {
            path: "/manager/change-password",
            name: "Change Password",
            icon: <FaLock />
        },
        {
            path: "/",
            name: "Logout",
            icon: <FaSignOutAlt />,
            onClick: () => {
                localStorage.clear();
            }
        }
    ];

    const renderMenuItem = (item) => {
        if (item.type === 'dropdown') {
            return (
                <div key={item.name} className="menu-dropdown">
                    <div 
                        className={`menu-item ${openDropdown === item.name.toLowerCase().replace(' ', '-') ? 'active' : ''}`} 
                        onClick={item.toggle}
                    >
                        <div className="menu-item-content">
                            <span className="icon">{item.icon}</span>
                            <span className="menu-text">
                                {item.name}
                                {item.name === 'Notices' && unreadCount > 0 && (
                                    <span className="notification-badge">
                                        {unreadCount}
                                    </span>
                                )}
                            </span>
                        </div>
                        <FaChevronDown className={`dropdown-arrow ${openDropdown === item.name.toLowerCase().replace(' ', '-') ? 'open' : ''}`} />
                    </div>
                    <div className={`dropdown-content ${openDropdown === item.name.toLowerCase().replace(' ', '-') ? 'show' : ''}`}>
                        {item.items.map((subItem) => (
                            <Link 
                                to={subItem.path} 
                                key={subItem.name}
                                className={`menu-item sub-item ${location.pathname === subItem.path ? 'active' : ''}`}
                                onClick={() => handleMenuClick(subItem)}
                            >
                                <div className="menu-item-content">
                                    <span className="icon">{subItem.icon}</span>
                                    <span className="menu-text">
                                        {subItem.name}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <Link 
                to={item.path} 
                key={item.name} 
                className={`menu-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleMenuClick(item)}
            >
                <div className="menu-item-content">
                    <span className="icon">{item.icon}</span>
                    <span className="menu-text">
                        {item.name}
                        {item.notification > 0 && (
                            <span className="notification-badge" style={{ 
                                marginLeft: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#e74c3c',
                                color: 'white',
                                borderRadius: '50%',
                                fontSize: '0.7rem',
                                minWidth: '18px',
                                height: '18px',
                                padding: '0 4px'
                            }}>
                                {item.notification}
                            </span>
                        )}
                    </span>
                </div>
            </Link>
        );
    };

    return (
        <div className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
                <img style={{width: '70px', height: '70px', backgroundColor: 'white', borderRadius: '10%'}} src={userProfile?.company_data?.logo} alt="Burray Logo" className="sidebar-logo" />
                <button className="mobile-close-btn" onClick={toggleMobileMenu}>
                    <FaTimes />
                </button>
            </div>
            <div className="sidebar-menu">
                {menuItems.map(item => renderMenuItem(item))}
            </div>
        </div>
    );
};

export default Sidebar;
